import { useQuery } from "@hooks/useQuery";
import { Pagination, TableColumnsType, Typography } from "antd";
import { ColumnType } from "antd/lib/table";
import { get as _get } from "lodash";
import moment from "moment";
import Papa from "papaparse";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import ConfirmationModal from "~/Components/Modal/confirmation-modal";
import { TableDrawer } from "~/Components/TableDrawer/TableDrawer";
import { useNotification } from "~/Hooks/useNotification";
import { eramanCMSApi, post, remove } from "~/Services";
import { eramanHost } from "~/Utils";
import ActionDropdown from "~/Utils/ActionDropdown";
import { decodeHTML } from "~/Utils/CommonUtils";
import { drilldownData, ModuleConfig } from "~/Utils/drilldownData";
import {
  renderActionsColumn,
  renderDateColumn,
  renderImageColumn,
  renderPublishedColumn,
  renderPublishedColumnStatus,
} from "~/Utils/TableRenderFunction";
import { useProductsDocumentDetails } from "./Products.hook";
const fieldToHeaderMap: Record<string, string> = {
  image: "Image",
  productCode: "Product Code",
  title: "Name (EN)",
  field_common_malay_title: "Name (MS)",
  field_common_mandarin_title: "Name (ZH)",
  category: "Category",
  subCategory: "Sub Category",
  brand: "Brand",
  dutyFree: "Duty Free",
  field_promotions: "Promotions",
  field_new_arrival: "New",
  field_best_seller: "Best Seller",
  status: "Published",
  body: "Description (EN)",
  field_common_malay_description: "Description (MS)",
  field_common_mandarin_descriptio: "Description (ZH)",
  field_product_terms_conditions: "Terms & Conditions (EN)",
  field_malay_terms_and_conditions: "Terms & Conditions (MS)",
  field_mandarin_terms_and_conditi: "Terms & Conditions (ZH)",
  field_promotion_title: "Promotion Title (EN)",
  field_malay_promotion_title: "Promotion Title (MS)",
  field_mandarin_promotion_title: "Promotion Title (ZH)",
  oldPrice: "Old Price",
  price: "Price",
  productOffer: "Discount Percentage",
  field_airport_export: "Airport",
  field_store_location_export: "Store Location",
  createdAt: "Created At",
  updatedAt: "Updated At",
};
function stripHTML(html: string): string {
  if (!html) return "";
  const decoded = decodeHTML(html);
  return decoded.replace(/<\/?[^>]+(>|$)/g, "").trim();
}

// Helper function to fetch all data
export const fetchAllData = async (apiUrl: string): Promise<any[]> => {
  const url = `${apiUrl}`;
  const response = await eramanCMSApi.get(url);
  if (!response.data) {
    throw new Error("No data received from API");
  }
  return response.data;
};

// Helper function to determine if a column is a ColumnType
function isColumnType<T>(column: ColumnType<T> | any): column is ColumnType<T> {
  return "dataIndex" in column;
}

// Helper function to get column definition by dataIndex
const getColumnByDataIndex = (columns: TableColumnsType<any>, dataIndex: string) => {
  return columns.find((col): col is ColumnType<any> => isColumnType(col) && col.dataIndex === dataIndex);
};

// Helper function to format export data
// ✅ Utility: format any value including arrays
const formatValue = (value: any, lookupData?: any[]) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (lookupData) {
          const match = lookupData?.find((d) => d?.value == item);
          return match ? match?.label : item;
        }
        return item;
      })
      .join("\n"); // <-- newline instead of comma
  }

  if (typeof value === "string") return stripHTML(value);
  return value ?? "";
};

const formatExportData = (
  exportData: any[],
  columns: TableColumnsType<any>,
  dateFields: string[] = [
    "createdAt",
    "updatedAt",
    "deletedAt",
    "expiryAt",
    "epcJoinedAt",
    "epcUpdatedAt",
    "epcViewedAt",
    "field_created_date",
    "field_updated_date",
  ],
  storeLocationsData: any[]
): any[] => {
  if (exportData.length === 0) return [];

  return exportData.map((record: any) => {
    const row: { [key: string]: any } = {};

    Object.keys(fieldToHeaderMap).forEach((field) => {
      let value = record[field];

      // ✅ Boolean-like fields → Yes / No
      if (["dutyFree", "field_promotions", "field_new_arrival", "field_best_seller", "status"].includes(field)) {
        value = value === true || value === "True" ? "Yes" : "No";
      }

      // ✅ Store Locations (array of IDs → labels with new line)
      else if (field === "field_store_location_export") {
        value = formatValue(record.field_store_location_export, storeLocationsData);
      }

      // ✅ Images (array of URLs → new line)
      else if (field === "image") {
        value = formatValue(value);
      }

      // ✅ Generic array fields
      else if (Array.isArray(value)) {
        value = formatValue(value);
      }

      // ✅ String fields → strip HTML
      else if (typeof value === "string") {
        value = stripHTML(value);
      }

      // ✅ Date fields
      if (dateFields.includes(field) && value) {
        value = moment(value).format("YYYY-MM-DD HH:mm:ss");
      }

      const header = fieldToHeaderMap[field] || field;
      row[header] = value ?? "";
    });

    return row;
  });
};

// Updated createExportHandler
export const createExportHandler = async (
  exportData: any[],
  columns: TableColumnsType<any>,
  exportFileName: string,
  storeLocationsData: any[]
): Promise<void> => {
  try {
    const formattedData = formatExportData(exportData, columns, undefined, storeLocationsData);

    if (formattedData.length === 0) {
      throw new Error("No data to export");
    }

    const csv = Papa.unparse(formattedData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${exportFileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error exporting data:", error);
    throw error;
  }
};

export const Products = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const [storeDropdownOptions, setStoreDropdownOptions] = useState<{ label: string; value: string }[]>([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState<string | undefined>(selectedRowData?.id);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);

  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);

  const [data, setData] = useState<any>([]);
  const [sortedData, setSortedData] = useState<any>([]);
  const currentPageData = sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [orderByName, setOrderByName] = useState<string>("id");

  const [subCategoriesData, setSubCategoriesData] = useState<any[]>([]);
  const [categoriesData, setCategoriesData] = useState<any[]>([]);
  const [brandData, setBrandData] = useState<any[]>([]);
  const [selectedCategoriesData, setSelectedCategoriesData] = useState<any>("");
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<any>(null);
  const [selectedBrand, setSelectedBrand] = useState<any>(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await eramanCMSApi.get(`/api/get-categories`);
        if (res?.data) {
          const allowedTids = [73, 74, 75];
          const data = res.data
            .filter((item: any) => allowedTids.includes(item.tid))
            .map((item: any) => ({
              label: decodeHTML(item.field_category_name),
              value: item.tid,
            }));
          setCategoriesData(data);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
        setCategoriesData([]);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchSubCategories = async () => {
      try {
        const url = selectedCategory
          ? `/api/get-all-subcategories/${selectedCategory}`
          : `/api/get-all-subcategories/all`;
        const res = await eramanCMSApi.get(url);
        if (res?.data) {
          const data = res.data.map((item: any) => ({
            label: decodeHTML(item.name),
            value: item.tid,
          }));
          setSubCategoriesData(data);
        }
      } catch (err) {
        console.error("Error fetching subcategories:", err);
        setSubCategoriesData([]);
      }
    };

    const fetchBrands = async () => {
      try {
        const url = selectedCategory
          ? `/api/all-brands/all-fields/published/${selectedCategory}`
          : `/api/all-brands/all-fields/published/all`;
        const res = await eramanCMSApi.get(url);
        if (res?.data) {
          const data = res.data.map((item: any) => ({
            label: decodeHTML(item.name),
            value: item.id,
          }));
          setBrandData(data);
        }
      } catch (err) {
        console.error("Error fetching brands:", err);
        setBrandData([]);
      }
    };

    fetchSubCategories();
    fetchBrands();
  }, [selectedCategory]);
  useEffect(() => {
    const fetchStoreDropdownData = async () => {
      try {
        const response = await eramanCMSApi.get(`/api/store-location-dropdown`);
        const data = response.data?.data || [];
        const options = data.map((item: { id: string; name: string }) => ({
          label: decodeHTML(item.name),
          value: item.id,
        }));
        setStoreDropdownOptions(options);
        console.log(storeDropdownOptions, "storeDropdownOptions");
      } catch (error) {
        console.error("Failed to fetch store dropdown:", error);
      }
    };
    fetchStoreDropdownData();
  }, []);

  const {
    data: drilldown,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchDocument,
  } = useProductsDocumentDetails(id || "");

  console.log("Products", selectedRowData);

  useEffect(() => {
    if (id) {
      refetchDocument();
    }
  }, [id]);

  useEffect(() => {
    const filteredData = data.filter((item: any) => {
      const searchLower = decodeHTML(searchValue).toLowerCase();
      return decodeHTML(item.title).toLowerCase().includes(searchLower);
    });
    setSortedData(filteredData);
    setTotalRecords(filteredData.length);
    setCurrentPage(0);
  }, [data, searchValue]);

  const getRecords = () => {
    setIsLoading(true);

    let category = selectedCategory || "all";
    let subCategory = selectedSubCategory || "all";
    let brand = selectedBrand || "all";

    const url = `/api/all-products/${category}/${subCategory}/${brand}`;
    // let url = `${eramanHost}/api/products/all-fields`;
    eramanCMSApi
      .get(url)
      .then((res: any) => {
        if (res && res.data) {
          let envelopes = res.data;
          setTotalRecords(envelopes.length);
          setData(envelopes);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setData([]);
        setIsLoading(false);
      });
  };

  const handleDelete = async (id: string) => {
    remove(`${eramanHost}/api/all-brands/${id}`)
      .then((res) => {
        getRecords();
      })
      .catch((err) => {});
  };

  const columns: TableColumnsType = [
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      render: renderImageColumn,
    },
    {
      title: "Name",
      dataIndex: "title",
      key: "title",
      render: (text) => decodeHTML(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "title"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "title"
            ? "descend"
            : undefined,
    },
    {
      title: "Product Code",
      dataIndex: "productCode",
      key: "productCode",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "productCode"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "productCode"
            ? "descend"
            : undefined,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (text) => decodeHTML(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "category"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "category"
            ? "descend"
            : undefined,
    },
    {
      title: "Sub Category",
      dataIndex: "subCategory",
      key: "subCategory",
      render: (text) => decodeHTML(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "subCategory"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "subCategory"
            ? "descend"
            : undefined,
    },
    {
      title: "Brand",
      dataIndex: "brand",
      key: "brand",
      render: (text) => decodeHTML(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "brand"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "brand"
            ? "descend"
            : undefined,
    },
    {
      title: "Duty Free",
      dataIndex: "dutyFree",
      key: "dutyFree",
      render: renderPublishedColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "dutyFree"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "dutyFree"
            ? "descend"
            : undefined,
    },
    {
      title: "Promo",
      dataIndex: "field_promotions",
      key: "field_promotions",
      render: renderPublishedColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "field_promotions"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "field_promotions"
            ? "descend"
            : undefined,
    },
    {
      title: "New",
      dataIndex: "field_new_arrival",
      key: "field_new_arrival",
      render: renderPublishedColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "field_new_arrival"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "field_new_arrival"
            ? "descend"
            : undefined,
    },
    {
      title: "Best Seller",
      dataIndex: "field_best_seller",
      key: "field_best_seller",
      render: renderPublishedColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "field_best_seller"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "field_best_seller"
            ? "descend"
            : undefined,
    },
    {
      title: "Published",
      dataIndex: "status",
      key: "status",
      render: renderPublishedColumnStatus,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "status"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "status"
            ? "descend"
            : undefined,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value: any) => renderDateColumn(value),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "createdAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "createdAt"
            ? "descend"
            : undefined,
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (value: any, record: any) => {
        if (value && record.createdAt && value === record.createdAt) {
          return "-";
        }
        return renderDateColumn(value);
      },
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "updatedAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "updatedAt"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_: any, record: any) => {
        return renderActionsColumn(
          record,
          setSelectedRowData,
          setDrawerVisible,
          (e: any) => {
            e.preventDefault();
            navigate(`/dedicated/eraman/product-management/products/add?editId=${record.id}`);
          },
          undefined,
          setDeleteModalVisible,
          "users"
        );
      },
    },
  ];

  useEffect(() => {
    getRecords();
  }, [selectedCategory, selectedSubCategory, selectedBrand]);

  useEffect(() => {
    setId(selectedRowData?.id);
  }, [selectedRowData]);

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";

    if (sortOrder === "asc" && orderByName === column.dataIndex) {
      setSortOrder("desc");
      order = "desc";
    } else {
      setSortOrder("asc");
      order = "asc";
    }

    setOrderByName(column.dataIndex);

    const tempData = [...data];
    setSortedData(tempData.sort((a: any, b: any) => handleSort(a, b, order, column.dataIndex)));
    setCurrentPage(0);
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    if (apiUrl === "") return;

    const payload = {
      action: action,
      method: method,
      module: "Product Management",
      subModule: "products",
      type: "DEDICATED",
      application: "ERAMAN",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "ERAMAN",
          apiUrl: apiUrl,
        },
      ],
      payload: data,
      status: status,
    };

    post("/management/publish", payload)
      .then((response: any) => {
        openToast({
          content: "Data Sent for Review",
          type: "success",
        });
        if (response.status === "OK" && status == "PENDING") {
          navigate("/dedicated/status-information?activeTab=pending");
        }
      })
      .catch((err: any) => {
        openToast({
          content: "Failed to send data",
          type: "error",
        });
      });
  };

  async function sendDeleteBrandRequest(reviewNotes: string) {
    try {
      await onSubmit(
        selectedRowData,
        `${eramanHost}/api/products/${selectedRowData?.id}`,
        "DELETE",
        "DELETE",
        "PENDING",
        reviewNotes
      );
    } catch (err) {}
  }

  const handleCategoriesChange = (value: any) => {
    setSelectedCategoriesData(value);
    setCurrentPage(0);
  };

  // Updated handleExportData
  async function handleExportData(isExportAll: boolean) {
    try {
      const baseApiUrl = `/api/all-products/${selectedCategory ?? "all"}/${selectedSubCategory ?? "all"}/${selectedBrand ?? "all"}`;
      let exportData: any[] = [];

      if (isExportAll) {
        // Fetch all data for export all
        exportData = await fetchAllData(baseApiUrl);
      } else {
        // Use currentPageData for selected rows or current page
        exportData =
          selectedRowKeys.length > 0
            ? currentPageData.filter((row: any) => selectedRowKeys.includes(row.id))
            : currentPageData;
      }

      if (exportData.length === 0) {
        openToast({
          content: "No data selected for export",
          type: "error",
        });
        return;
      }

      await createExportHandler(exportData, columns, "ERAMAN_PRODUCT_LIST", storeDropdownOptions);
    } catch (error) {
      openToast({
        content: "Failed to export data",
        type: "error",
      });
    }
  }

  return (
    <>
      <DataTable
        tabOffset={60}
        columns={columns.map((col) => ({
          ...col,
          onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
        }))}
        multiSelect={true}
        className="table-clickable"
        tableData={
          currentPageData
            ? currentPageData?.map((d: any) => {
                return {
                  ...d,
                  key: d.id,
                  title: decodeHTML(d.title),
                };
              })
            : []
        }
        isLoading={isLoading}
        exportFileName="EPC_APPLICATION"
        exportHandler={handleExportData}
        selectedRowKeys={selectedRowKeys}
        onSelectionChange={setSelectedRowKeys}
        totalRecords={totalRecords}
        isPagination={true}
        recordsPerPage={10}
        onSearchChange={(searchText: string) => {
          setSearchValue(searchText);
          setCurrentPage(0);
        }}
        hideExport={false}
        isServerSearchApi={false}
        searchPlaceholderText={"Search Name"}
        onClick={(record) => {
          setSelectedRowData(record);
        }}
        additionalCTA={
          <div className="flex items-center space-x-2">
            {/* Category Dropdown */}
            <ActionDropdown
              value={selectedCategory}
              options={categoriesData}
              onChange={(value) => {
                setSelectedCategory(value);
                setSelectedSubCategory(null); // reset on category change
                setSelectedBrand(null);
              }}
              placeholder="Select Category"
              className="w-48"
            />

            {/* Subcategory Dropdown */}
            <ActionDropdown
              value={selectedSubCategory}
              options={subCategoriesData}
              onChange={(value) => {
                console.log("Sub selected:", value);
                setSelectedSubCategory(value);
              }}
              placeholder="Select Subcategory"
              className="w-48"
            />

            {/* Brand Dropdown */}
            <ActionDropdown
              value={selectedBrand}
              options={brandData}
              onChange={(value) => {
                console.log("Brand selected:", value);
                setSelectedBrand(value); // value is number
              }}
              placeholder="Select Brand"
              className="w-48"
            />
          </div>
        }
      />
      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          current={currentPage + 1}
          total={totalRecords}
          pageSize={pageSize}
          onChange={(curPage, newPageSize) => {
            setCurrentPage(curPage - 1);
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
              setCurrentPage(0);
            }
          }}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
          // onChange={(curPage) => {
          //   setCurrentPage(curPage - 1);
          // }}
        />
      </div>

      <TableDrawer
        title={
          <Typography.Title
            level={4}
            className={"!mb-0"}
          >
            Detailed View
          </Typography.Title>
        }
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        fields={drilldownData[_get(drilldown, "subModule", "products") as ModuleConfig]}
        data={drilldown}
      />

      <ConfirmationModal
        type="delete"
        open={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onSubmit={sendDeleteBrandRequest}
      />
    </>
  );
};

export function handleSort(a: any, b: any, order: string | undefined, sortBy: string) {
  // Special handling for field_faqs_question (if applicable)
  if (sortBy === "field_faqs_question") {
    const numberA = extractNumber(a[sortBy]);
    const numberB = extractNumber(b[sortBy]);

    if (order === "desc") {
      return numberA < numberB ? 1 : -1;
    } else if (order === "asc") {
      return numberA > numberB ? 1 : -1;
    } else {
      return 0;
    }
  }
  if (sortBy === "title") {
    const valueA = a[sortBy] ? String(a[sortBy]).toLowerCase() : "";
    const valueB = b[sortBy] ? String(b[sortBy]).toLowerCase() : "";

    if (order === "desc") {
      return valueA < valueB ? 1 : -1;
    } else if (order === "asc") {
      return valueA > valueB ? 1 : -1;
    } else {
      return 0;
    }
  }
  if (order === "desc") {
    return a[sortBy] < b[sortBy] ? 1 : -1;
  } else if (order === "asc") {
    return a[sortBy] > b[sortBy] ? 1 : -1;
  } else {
    return 0;
  }
}
export function extractNumber(question: string): number {
  const match = question.match(/Q(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}
